import { c as _c } from "react/compiler-runtime";
import { queryClient } from "@/config/react-query";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import { clinicClockFor } from "@/lib/clinic-clock";
import { toMs } from "@/lib/timestamp";
import { isValidTimezone, useStoreTimezone } from "@/lib/timezone";
import type { AlertDetail, AppointmentChecklistUpdatedData } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import { UserType } from "@/types/enums/user-type";
import type { AppointmentChecklistItem, PatientStatusEventResponse } from "@/types/event";
import { eventQueryKeys } from "@/query/check-in/calendar/event-query";
const CHECKLIST_ALERT_TYPES = [AlertType.APPOINTMENT_CHECKLIST_FILLED_FORM, AlertType.APPOINTMENT_CHECKLIST_CHECKED_IN, AlertType.APPOINTMENT_CHECKLIST_WITH_MA, AlertType.APPOINTMENT_CHECKLIST_MEASUREMENT_TAKEN, AlertType.APPOINTMENT_CHECKLIST_DOSE_TAKEN, AlertType.APPOINTMENT_CHECKLIST_WITH_PROVIDER, AlertType.APPOINTMENT_CHECKLIST_NOTE_TAKEN, AlertType.APPOINTMENT_CHECKLIST_PAYMENT_MADE, AlertType.APPOINTMENT_CHECKLIST_REQUEST_GFE, AlertType.APPOINTMENT_CHECKLIST_PICTURE_TAKEN, AlertType.APPOINTMENT_CHECKLIST_CARE_PLAN, AlertType.APPOINTMENT_CHECKLIST_NOTE_SIGNED_OFF, AlertType.APPOINTMENT_CHECKLIST_REBOOK, AlertType.APPOINTMENT_CHECKLIST_PROFILE_PHOTO, AlertType.APPOINTMENT_CHECKLIST_REOPENED];
function buildUpdatedChecklistItem(item: AppointmentChecklistItem, eventId: string): AppointmentChecklistItem {
  return {
    ...item,
    eventId,
    completedDate: item.completedDate ?? undefined
  };
}
function updateEventChecklistCache(eventId: string, updated: AppointmentChecklistItem): void {
  const eventKey = eventQueryKeys.appointmentChecklist(eventId);
  queryClient.setQueryData<AppointmentChecklistItem[] | undefined>(eventKey, prev => {
    if (!prev) return prev;
    const exists = prev.some(i => i.id === updated.id);
    // Alerts never carry a Rebook step's recommended follow-ups, so keep the
    // ones already loaded or the step loses them until the next refetch.
    return exists ? prev.map(i => i.id === updated.id ? {
      ...updated,
      recommendedServices: i.recommendedServices
    } : i) : [...prev, updated];
  });
}

/**
 * `dependencyCompleted`, when given, is the item's new completion state. Rows
 * that depend on the item get their `dependsOn` snapshot patched to match, so
 * a step hidden from Patient Status still unlocks the visible step after it.
 */
export function updatePatientStatusCacheEntries(eventId: string, checklistItemId: string, updater: (item: AppointmentChecklistItem) => AppointmentChecklistItem, dependencyCompleted?: boolean, checklistSnapshot?: AppointmentChecklistItem[]): {
  foundInCache: boolean;
  previous: [readonly unknown[], PatientStatusEventResponse[] | undefined][];
} {
  const allPatientStatusQueries = queryClient.getQueriesData<PatientStatusEventResponse[]>({
    queryKey: [...eventQueryKeys.all, "patientStatus"]
  });
  let foundInCache = false;
  const previous: [readonly unknown[], PatientStatusEventResponse[] | undefined][] = [];
  for (const [queryKey, data] of allPatientStatusQueries) {
    if (!data) continue;
    const eventEntry = data.find(d => d.event.id === eventId);
    if (eventEntry) {
      foundInCache = true;
      previous.push([queryKey, data]);
      queryClient.setQueryData<PatientStatusEventResponse[]>(queryKey, data.map(d => {
        if (d.event.id !== eventId) return d;
        const existingIds = new Set(d.checklist.map(i => i.id));
        const rows = [...d.checklist, ...(checklistSnapshot ?? []).filter(i => !existingIds.has(i.id))];
        return {
          ...d,
          // Snapshots supply missing intermediates, not newer state for
          // unrelated cached rows: alerts can arrive out of order.
          checklist: rows.map(i => {
            const previousItem = d.checklist.find(previous => previous.id === i.id);
            const preserved = previousItem ? {
              ...i,
              recommendedServices: previousItem.recommendedServices
            } : i;
            const next = i.id === checklistItemId ? updater(preserved) : preserved;
            if (dependencyCompleted === undefined || !next.dependsOn?.some(dep => dep.id === checklistItemId)) {
              return next;
            }
            return {
              ...next,
              dependsOn: next.dependsOn.map(dep => dep.id === checklistItemId ? {
                ...dep,
                completed: dependencyCompleted
              } : dep)
            };
          })
        };
      }));
    }
  }
  return {
    foundInCache,
    previous
  };
}
export function removeEventFromPatientStatusCache(eventId: string): void {
  const allPatientStatusQueries = queryClient.getQueriesData<PatientStatusEventResponse[]>({
    queryKey: [...eventQueryKeys.all, "patientStatus"]
  });
  for (const [queryKey, data] of allPatientStatusQueries) {
    if (!data) continue;
    if (!data.some(d => d.event.id === eventId)) continue;
    queryClient.setQueryData<PatientStatusEventResponse[]>(queryKey, data.filter(d => d.event.id !== eventId));
  }
}
function seedNewPatientStatusEntry(payload: AppointmentChecklistUpdatedData, /** The viewer's zone, for a cached query whose key carries none. */
fallbackTimeZone: string): void {
  if (!payload.event) return;
  const event = payload.event;

  // Every row in this cache is rendered as a patient, so an event with no
  // patient attendee has nothing to show and would surface as a patientless
  // row. Skip it: the next refetch of the patient-status query returns the
  // complete record from the API.
  if (!event.attendees?.some(a => a.type === UserType.PATIENT)) return;
  const newEntry: PatientStatusEventResponse = {
    event: {
      ...event
    },
    checklist: payload.checklist.map(item => ({
      ...item,
      completedDate: item.completedDate ? item.completedDate : undefined
    }))
  };
  const allPatientStatusQueries = queryClient.getQueriesData<PatientStatusEventResponse[]>({
    queryKey: [...eventQueryKeys.all, "patientStatus"]
  });
  for (const [queryKey, data] of allPatientStatusQueries) {
    // Key structure: [...eventQueryKeys.all, "patientStatus", timezone,
    // { locationIds }]. Only events touching that zone's today belong.
    const keyZone = queryKey[queryKey.length - 2];
    const clock = clinicClockFor(typeof keyZone === "string" && isValidTimezone(keyZone) ? keyZone : fallbackTimeZone);
    const today = clock.dayRange(clock.today());
    if (toMs(event.end) < today.start.getTime() || toMs(event.start) >= today.end.getTime()) continue;

    // Check location filter from query key to avoid cross-location seeding.
    const keyMeta = queryKey[queryKey.length - 1] as {
      locationIds?: string[];
    } | null;
    const filterIds = keyMeta?.locationIds;
    if (filterIds && event.locationId && !filterIds.includes(event.locationId)) {
      continue;
    }
    queryClient.setQueryData<PatientStatusEventResponse[]>(queryKey, data ? [...data, newEntry] : [newEntry]);
  }
}
export function useChecklistRealtimeCache() {
  const $ = _c(2);
  const timeZone = useStoreTimezone();
  let t0;
  if ($[0] !== timeZone) {
    t0 = function handleMessage(message) {
      const payload = message.data as AppointmentChecklistUpdatedData;
      const item = payload.appointmentChecklistItem;
      if (!item) {
        return;
      }
      const updated = buildUpdatedChecklistItem(item, payload.eventId);
      const patientStatusItem = payload.checklist.find(i => i.id === updated.id);
      const patientStatusUpdated = buildUpdatedChecklistItem(patientStatusItem ?? item, payload.eventId);
      updateEventChecklistCache(payload.eventId, updated);
      const {
        foundInCache
      } = updatePatientStatusCacheEntries(payload.eventId, patientStatusUpdated.id, previous => ({
        ...patientStatusUpdated,
        recommendedServices: previous.recommendedServices
      }), !!patientStatusUpdated.completedDate, payload.checklist.map(row => buildUpdatedChecklistItem(row, payload.eventId)));
      if (!foundInCache) {
        seedNewPatientStatusEntry(payload, timeZone);
      }
    };
    $[0] = timeZone;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleMessage = t0;
  useWebSocketCallbackSubscription(CHECKLIST_ALERT_TYPES, handleMessage, true, "useChecklistRealtimeCache");
}